import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "themes/driver-theme.css";

import { useAuth } from "hooks/useAuth";
import { getAnonymousUUID } from "utils/storage.utils";
import { TOUR_IDS, TOUR_LABELS } from "utils/constants";
import {
    getStepsForTour,
    getTourIdForPath,
    isTourCompleted,
    setTourCompleted,
    clearTourCompleted,
    TOUR_DRIVER_CONFIG,
} from "utils/tour.utils";

const TourContext = createContext(null);

const AUTO_START_DELAY_MS = 1800;

const filterAvailableSteps = (steps) =>
    steps.filter((step) => {
        if (!step.element) return true;
        try {
            return !!document.querySelector(step.element);
        } catch {
            return false;
        }
    });

const createDriverInstance = ({ steps, onComplete, onDismiss }) => {
    const activeSteps = filterAvailableSteps(steps);
    if (!activeSteps.length) return null;

    let completionHandled = false;
    const handleCompletion = () => {
        if (completionHandled) return;
        completionHandled = true;
        onComplete?.();
    };

    const driverObj = driver({
        ...TOUR_DRIVER_CONFIG,
        showProgress: true,
        animate: true,
        allowClose: true,
        overlayOpacity: 0.72,
        stagePadding: 10,
        stageRadius: 10,
        popoverClass: "theecastable-tour-popover",
        progressText: "{{current}} of {{total}}",
        nextBtnText: "Next →",
        prevBtnText: "← Previous",
        doneBtnText: "Done",
        steps: activeSteps,
        onHighlighted: (element, _step, { state }) => {
            /* Mark seen as soon as the tour is shown so prompt navigation won't re-trigger it */
            if (state.activeIndex === 0) {
                onDismiss?.();
            }
            /* Scroll stacked mobile layout so Similar Prompts is visible in the spotlight */
            if (element?.matches?.('[data-tour="similar-prompts"]')) {
                element.scrollIntoView({ block: "center", behavior: "instant" });
            }
        },
        onCloseClick: (_element, _step, { driver: d }) => {
            handleCompletion();
            d.destroy();
        },
        onDestroyed: () => {
            handleCompletion();
        },
    });

    return driverObj;
};

export const TourProvider = ({ children }) => {
    const { user } = useAuth();
    const location = useLocation();
    const driverRef = useRef(null);
    const autoStartTimerRef = useRef(null);
    const tourId = getTourIdForPath(location.pathname);
    const userId =
        user?._id ||
        user?.id ||
        user?.uuid ||
        (!user ? getAnonymousUUID() : null);

    const destroyActiveTour = useCallback(() => {
        if (driverRef.current?.isActive?.()) {
            driverRef.current.destroy();
        }
        driverRef.current = null;
    }, []);

    const startTour = useCallback(
        (tourId, { force = false, markComplete = true } = {}) => {
            if (!tourId) return false;

            const steps = getStepsForTour(tourId);
            if (!steps.length) return false;

            if (!force && userId && isTourCompleted(userId, tourId)) {
                return false;
            }

            destroyActiveTour();

            const driverObj = createDriverInstance({
                steps,
                onDismiss: () => {
                    if (markComplete && userId) {
                        setTourCompleted(userId, tourId);
                    }
                },
                onComplete: () => {
                    if (markComplete && userId) {
                        setTourCompleted(userId, tourId);
                    }
                    driverRef.current = null;
                },
            });

            if (!driverObj) return false;

            driverRef.current = driverObj;

            requestAnimationFrame(() => {
                setTimeout(() => driverObj.drive(), 120);
            });

            return true;
        },
        [destroyActiveTour, userId]
    );

    const startTourForPath = useCallback(
        (pathname, options) => {
            const tourId = getTourIdForPath(pathname);
            if (!tourId) return false;
            return startTour(tourId, options);
        },
        [startTour]
    );

    const resetTour = useCallback(
        (tourId) => {
            if (userId && tourId) {
                clearTourCompleted(userId, tourId);
            }
            startTour(tourId, { force: true });
        },
        [startTour, userId]
    );

    /* Destroy active tour only when leaving a tour context (not on prompt-to-prompt navigation) */
    useEffect(() => {
        return () => {
            if (driverRef.current?.isActive?.()) {
                driverRef.current.destroy();
            }
        };
    }, [tourId]);

    /* Auto-start tour on first visit to a supported page (keyed by tourId, not pathname) */
    useEffect(() => {
        if (autoStartTimerRef.current) {
            clearTimeout(autoStartTimerRef.current);
            autoStartTimerRef.current = null;
        }

        if (!tourId) return undefined;

        /* Guests can still tour prompts; other pages require login */
        const guestAllowed = tourId === TOUR_IDS.PROMPTS;
        if (!userId && !guestAllowed) return undefined;

        if (userId && isTourCompleted(userId, tourId)) return undefined;

        autoStartTimerRef.current = setTimeout(() => {
            startTour(tourId, { force: false, markComplete: true });
        }, AUTO_START_DELAY_MS);

        return () => {
            if (autoStartTimerRef.current) {
                clearTimeout(autoStartTimerRef.current);
            }
        };
    }, [tourId, userId, startTour]);

    useEffect(() => () => destroyActiveTour(), [destroyActiveTour]);

    const value = {
        startTour,
        startTourForPath,
        resetTour,
        getTourIdForPath,
        getTourLabel: (tourId) => TOUR_LABELS[tourId] ?? "Guided Tour",
        isTourCompleted: (tourId) =>
            userId ? isTourCompleted(userId, tourId) : false,
        currentTourId: tourId,
    };

    return (
        <TourContext.Provider value={value}>{children}</TourContext.Provider>
    );
};

export const useTour = () => {
    const ctx = useContext(TourContext);
    if (!ctx) {
        throw new Error("useTour must be used within a TourProvider");
    }
    return ctx;
};

export default TourProvider;
